// Sync between the local backend and a remote one (decision 0033: "conflicts: the newer save wins, and the player is
// asked when both changed since the last sync"). The sync remembers, per key, the revision both sides agreed on last
// time (the base, kept in the local backend under "__sync"). Then for each key:
//   - same revision on both sides: nothing to do;
//   - one side missing: copy the other over;
//   - only one side changed since the base: that side is the newer save, and wins;
//   - both changed (or there is no base yet and they differ): a conflict. The key's policy decides: "ask" puts the
//     choice to the player (the game's `ask`), "newer" keeps the later `savedAt` (ties keep the local one), "merge"
//     joins both with the game's `merge` and writes the result to both sides (a record whose changes never conflict:
//     unlocks, a codex, retired tips).
// Deleting a record doesn't sync yet: a key missing on one side is filled from the other. A remote that fails
// (offline) leaves the local records alone and reports "offline".
import { fromJsonBytes, jsonBytes } from "./codec";
import { infoOf, revOf, type RecordInfo, type SaveBackend, type SaveRecord } from "./backend";

/** How a key's conflicts resolve. */
export type SyncPolicy = "ask" | "newer" | "merge";

/** Both sides changed since the last sync. */
export interface SyncConflict {
  key: string;
  local: RecordInfo;
  remote: RecordInfo;
  /** Which side was written later (by `savedAt`). */
  newer: "local" | "remote" | "same";
}

/** What the player (or the policy) chose. */
export type SyncChoice = "local" | "remote";

/** What a sync did for one key. */
export interface SyncOutcome {
  key: string;
  action: "in-sync" | "pushed" | "pulled" | "conflict-kept-local" | "conflict-took-remote" | "merged" | "offline";
  conflict?: SyncConflict;
  error?: string;
}

/** Options for {@link createSync}. */
export interface SyncOptions {
  local: SaveBackend;
  remote: SaveBackend;
  /** Ask the player which save to keep (only for keys whose policy is "ask"). */
  ask(conflict: SyncConflict): Promise<SyncChoice>;
  /** The policy per key (default "ask" for every key). */
  policy?: (key: string) => SyncPolicy;
  /** Join both sides' bytes for a key whose policy is "merge" (the result is written to both). Required for "merge". */
  merge?: (key: string, local: Uint8Array, remote: Uint8Array) => Uint8Array;
  /** The local key the bases are kept under (default {@link SYNC_BASE_KEY}); one per remote, so switching remotes
   *  (a stub, then a signed-in account) doesn't compare one remote's records against another's bases. Must start "__". */
  baseKey?: string;
}

/** The sync. */
export interface Sync {
  /** Sync one key. */
  sync(key: string): Promise<SyncOutcome>;
  /** Sync every key either side has, one after another. */
  syncAll(): Promise<SyncOutcome[]>;
  /** The revisions both sides agreed on, per key. */
  base(): Promise<Record<string, string>>;
}

/** The local key the sync keeps its bases under. */
export const SYNC_BASE_KEY = "__sync";

/** Build a sync between a local and a remote backend. Runs are queued, never overlapping. */
export function createSync(o: SyncOptions): Sync {
  const policy = o.policy ?? (() => "ask" as const);
  const baseKey = o.baseKey ?? SYNC_BASE_KEY;
  if (!baseKey.startsWith("__")) throw new Error(`sync base key ${baseKey} must start with "__"`);
  let queue: Promise<unknown> = Promise.resolve();
  const serial = <T>(f: () => Promise<T>): Promise<T> => {
    const run = queue.then(f, f);
    queue = run.catch(() => undefined);
    return run;
  };

  const readBase = async (): Promise<Record<string, string>> => {
    const r = await o.local.get(baseKey);
    return r ? fromJsonBytes<Record<string, string>>(r.bytes) : {};
  };
  const writeBase = async (key: string, rev: string | null): Promise<void> => {
    const base = await readBase();
    if (rev === null) delete base[key];
    else base[key] = rev;
    const bytes = jsonBytes(base);
    await o.local.put({ key: baseKey, bytes, savedAt: Date.now(), rev: revOf(bytes), meta: {} });
  };

  const one = async (key: string): Promise<SyncOutcome> => {
    const local = await o.local.get(key);
    let remote: SaveRecord | null;
    try {
      remote = await o.remote.get(key);
    } catch (e) {
      return { key, action: "offline", error: e instanceof Error ? e.message : String(e) };
    }
    const push = async (r: SaveRecord, action: SyncOutcome["action"], conflict?: SyncConflict): Promise<SyncOutcome> => {
      try {
        await o.remote.put(r);
      } catch (e) {
        return { key, action: "offline", error: e instanceof Error ? e.message : String(e), conflict };
      }
      await writeBase(key, r.rev);
      return conflict ? { key, action, conflict } : { key, action };
    };
    const pull = async (r: SaveRecord, action: SyncOutcome["action"], conflict?: SyncConflict): Promise<SyncOutcome> => {
      await o.local.put(r);
      await writeBase(key, r.rev);
      return conflict ? { key, action, conflict } : { key, action };
    };

    if (!local && !remote) return { key, action: "in-sync" };
    if (local && remote && local.rev === remote.rev) {
      await writeBase(key, local.rev);
      return { key, action: "in-sync" };
    }
    if (!remote) return push(local!, "pushed");
    if (!local) return pull(remote, "pulled");

    const base = (await readBase())[key];
    const localChanged = local.rev !== base;
    const remoteChanged = remote.rev !== base;
    if (localChanged && !remoteChanged) return push(local, "pushed");
    if (remoteChanged && !localChanged) return pull(remote, "pulled");

    const conflict: SyncConflict = {
      key,
      local: infoOf(local),
      remote: infoOf(remote),
      newer: local.savedAt === remote.savedAt ? "same" : local.savedAt > remote.savedAt ? "local" : "remote",
    };
    if (policy(key) === "merge") {
      if (!o.merge) throw new Error(`sync: key "${key}" has the merge policy but no merge function was given`);
      const bytes = o.merge(key, local.bytes, remote.bytes);
      const merged: SaveRecord = { key, bytes, savedAt: Math.max(local.savedAt, remote.savedAt), rev: revOf(bytes), meta: { ...remote.meta, ...local.meta } };
      try {
        await o.remote.put(merged);
      } catch (e) {
        return { key, action: "offline", error: e instanceof Error ? e.message : String(e), conflict };
      }
      await o.local.put(merged);
      await writeBase(key, merged.rev);
      return { key, action: "merged", conflict };
    }
    const choice: SyncChoice = policy(key) === "newer" ? (conflict.newer === "remote" ? "remote" : "local") : await o.ask(conflict);
    return choice === "local" ? push(local, "conflict-kept-local", conflict) : pull(remote, "conflict-took-remote", conflict);
  };

  return {
    sync: (key) => serial(() => one(key)),
    syncAll: () =>
      serial(async () => {
        let remoteKeys: string[];
        try {
          remoteKeys = (await o.remote.list()).map((r) => r.key);
        } catch (e) {
          return [{ key: "*", action: "offline" as const, error: e instanceof Error ? e.message : String(e) }];
        }
        const keys = [...new Set([...(await o.local.list()).map((r) => r.key), ...remoteKeys])].sort();
        const out: SyncOutcome[] = [];
        for (const k of keys) out.push(await one(k));
        return out;
      }),
    base: readBase,
  };
}
